import { useState } from "react";
import { trpc } from "../lib/trpc.js";
import {
  Bot, Wifi, WifiOff, Loader2, AlertTriangle, Activity,
  CheckCircle2, Clock, Zap, TrendingUp, X, ArrowLeft,
  Filter, Search, BarChart3, Settings2, RefreshCw,
  FileText, Brain, Heart, Code, ChevronRight, ChevronDown,
  Eye, Cpu, Sparkles, Wrench, Copy, Check, FolderOpen, Pencil, Save
} from "lucide-react";

// ─── Shared Types & Config ──────────────────────────────────────────
const statusConfig: Record<string, { color: string; bg: string; border: string; icon: typeof Wifi; label: string; dot: string }> = {
  online:  { color: "text-success",  bg: "bg-success/8",  border: "border-success/20", icon: Wifi,          label: "Online",   dot: "bg-success animate-pulse" },
  busy:    { color: "text-primary",    bg: "bg-primary/8",    border: "border-primary/20",   icon: Activity,      label: "Ocupado",  dot: "bg-primary animate-pulse" },
  offline: { color: "text-muted-foreground", bg: "bg-muted/30", border: "border-border",     icon: WifiOff,       label: "Offline",  dot: "bg-muted-foreground" },
  error:   { color: "text-red-400",    bg: "bg-red-500/8",    border: "border-red-500/20",   icon: AlertTriangle, label: "Erro",     dot: "bg-red-400 animate-pulse" },
};

const agentTypeLabels: Record<string, string> = {
  deployer:  "Deploy & Infra",
  publisher: "Publicação",
  analyst:   "Análise de Dados",
  seo:       "SEO & Otimização",
  creative:  "Criativos",
  monitor:   "Monitoramento",
  arbitrage: "Arbitragem",
  social:    "Redes Sociais",
  funnel:    "Funil de Vendas",
  critic:    "Qualidade",
  broadcast: "Distribuição",
  megan:     "Orquestradora",
  strategy:  "Estratégia",
};

const agentTypeIcons: Record<string, string> = {
  deployer: "🚀", publisher: "📢", analyst: "📊", seo: "🔍",
  creative: "🎨", monitor: "👁️", arbitrage: "💰", social: "📱",
  funnel: "🔄", critic: "✅", broadcast: "📡", megan: "🧠", strategy: "📋",
};

// ─── Utility Functions ──────────────────────────────────────────────
function SuccessBar({ rate }: { rate: number }) {
  const color = rate >= 80 ? "bg-success" : rate >= 50 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="w-full bg-border rounded-full h-1.5 mt-1.5 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ${color}`}
        style={{ width: `${Math.min(rate, 100)}%` }}
      />
    </div>
  );
}

function formatDuration(ms: number | null | undefined): string {
  if (!ms) return "—";
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
}

function formatTimeAgo(ts: number | null | undefined): string {
  if (!ts) return "Nunca";
  const diff = Date.now() - ts;
  if (diff < 60000) return "Agora";
  if (diff < 3600000) return `${Math.round(diff / 60000)}m atrás`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h atrás`;
  return `${Math.round(diff / 86400000)}d atrás`;
}

// ─── Agent Detail View ──────────────────────────────────────────────
function AgentDetail({ agentId, onBack }: { agentId: string; onBack: () => void }) {
  const { data: agent, isLoading } = trpc.agents.getById.useQuery({ id: agentId });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-2/3" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-muted rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="space-y-6">
        <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <p className="text-muted-foreground">Agente não encontrado</p>
        </div>
      </div>
    );
  }

  const cfg = statusConfig[agent.status] || statusConfig.offline;
  const successRate = agent.successRate || 0;
  const config = (agent.config || {}) as Record<string, unknown>;

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar para Agentes
      </button>

      {/* Agent Header */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${cfg.bg}`}>
              {agentTypeIcons[agent.type] || "🤖"}
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{agent.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm text-muted-foreground">{agentTypeLabels[agent.type] || agent.type}</span>
                <div className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.color} ${cfg.border} bg-transparent`}>
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
                  {cfg.label}
                </div>
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <p>Última atividade: {formatTimeAgo(agent.lastActiveAt)}</p>
            <p className="mt-0.5">Criado: {new Date(agent.createdAt).toLocaleDateString("pt-BR")}</p>
          </div>
        </div>
        {agent.description && (
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{agent.description}</p>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-success" />
            <span className="text-xs text-muted-foreground">Tasks Completas</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{agent.tasksCompleted || 0}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Taxa de Sucesso</span>
          </div>
          <p className={`text-2xl font-bold ${successRate >= 80 ? "text-success" : successRate >= 50 ? "text-yellow-400" : "text-red-400"}`}>
            {successRate}%
          </p>
          <SuccessBar rate={successRate} />
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-muted-foreground">Duração Média</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatDuration(agent.avgDuration)}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span className="text-xs text-muted-foreground">Última Atividade</span>
          </div>
          <p className="text-lg font-bold text-foreground">{formatTimeAgo(agent.lastActiveAt)}</p>
        </div>
      </div>

      {/* Configuration */}
      {Object.keys(config).length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
            <Settings2 className="w-4 h-4 text-muted-foreground" />
            Configuração
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(config).map(([key, value]) => (
              <div key={key} className="bg-muted/30 border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground font-mono">{key}</p>
                <p className="text-sm text-foreground mt-0.5 font-mono truncate">
                  {typeof value === "object" ? JSON.stringify(value) : String(value)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Brain Agent Mapping */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-muted-foreground" />
          Mapeamento Brain
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-muted/30 border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Tipo</p>
            <p className="text-sm text-foreground font-medium mt-0.5">{agentTypeLabels[agent.type] || agent.type}</p>
          </div>
          <div className="bg-muted/30 border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">ID Brain</p>
            <p className="text-sm text-foreground font-mono mt-0.5">{agent.type}</p>
          </div>
          <div className="bg-muted/30 border border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Status Sync</p>
            <p className="text-sm text-foreground mt-0.5 flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${agent.status === "online" ? "bg-success" : "bg-muted-foreground"}`} />
              {agent.status === "online" ? "Sincronizado" : "Desconectado"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── File Viewer/Editor Modal ──────────────────────────────────────
function FileViewer({
  file,
  agentName,
  agentId,
  onClose,
}: {
  file: { name: string; type: string; content: string; size: number };
  agentName: string;
  agentId?: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(file.content);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const utils = trpc.useUtils();
  const updateFile = trpc.brain.updateAgentFile.useMutation({
    onSuccess: () => {
      setSaving(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      // Invalidate the cache so the list refreshes
      utils.brain.getAgentsWithFiles.invalidate();
    },
    onError: () => {
      setSaving(false);
    },
  });

  const getLanguageLabel = (type: string) => {
    switch (type) {
      case "markdown": return "Markdown";
      case "python": return "Python";
      case "typescript": return "TypeScript";
      case "json": return "JSON";
      default: return "Text";
    }
  };

  const getLanguageColor = (type: string) => {
    switch (type) {
      case "markdown": return "text-blue-400";
      case "python": return "text-yellow-400";
      case "typescript": return "text-blue-300";
      case "json": return "text-success";
      default: return "text-muted-foreground";
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editing ? editContent : file.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = () => {
    if (!agentId) return;
    setSaving(true);
    updateFile.mutate({ agentId, fileName: file.name, content: editContent });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Bot className="w-5 h-5 text-primary" />
            <span className="text-muted-foreground">{agentName}</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
            <span className={`font-mono text-sm ${getLanguageColor(file.type)}`}>
              {file.name}
            </span>
            <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">
              {getLanguageLabel(file.type)}
            </span>
            <span className="text-xs text-muted-foreground">
              {(file.size / 1024).toFixed(1)} KB
            </span>
            {editing && (
              <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded border border-primary/20">
                Editando
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {editing && agentId && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : saveSuccess ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                {saving ? "Salvando..." : saveSuccess ? "Salvo!" : "Salvar"}
              </button>
            )}
            {agentId && (
              <button
                onClick={() => {
                  if (editing) {
                    setEditContent(file.content);
                  }
                  setEditing(!editing);
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  editing
                    ? "bg-primary/10 text-primary hover:bg-primary/20"
                    : "hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                }`}
                title={editing ? "Cancelar edição" : "Editar arquivo"}
              >
                {editing ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
              </button>
            )}
            <button
              onClick={handleCopy}
              className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
              title="Copiar conteúdo"
            >
              {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {editing ? (
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full h-full min-h-[400px] p-4 bg-transparent text-sm font-mono text-foreground/80 leading-relaxed resize-none focus:outline-none"
              spellCheck={false}
            />
          ) : (
            <pre className="text-sm font-mono text-foreground/80 leading-relaxed">
              <code>
                {file.content.split("\n").map((line, i) => (
                  <div key={i} className="flex hover:bg-muted/30">
                    <span className="select-none text-muted-foreground/30 text-right w-12 pr-4 py-0.5 flex-shrink-0 border-r border-border/50">
                      {i + 1}
                    </span>
                    <span className="pl-4 py-0.5 flex-1 whitespace-pre-wrap break-all">
                      {line || " "}
                    </span>
                  </div>
                ))}
              </code>
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Agent File Card ────────────────────────────────────────────────
function AgentFileCard({
  agent,
  onViewFile,
}: {
  agent: {
    id?: string;
    name: string;
    description: string;
    files: Array<{ name: string; type: string; size: number; content: string }>;
    hasMemory: boolean;
    hasSoul: boolean;
    hasCode: boolean;
  };
  onViewFile: (file: { name: string; type: string; size: number; content: string }, agentName: string, agentId?: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const getFileIcon = (type: string) => {
    switch (type) {
      case "markdown": return <FileText className="w-4 h-4 text-blue-400" />;
      case "python": return <Code className="w-4 h-4 text-yellow-400" />;
      case "typescript": return <Code className="w-4 h-4 text-blue-300" />;
      default: return <FileText className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getFileLabel = (name: string) => {
    if (name === "AGENT.md") return "Configuração";
    if (name === "MEMORY.md") return "Memória";
    if (name === "SOUL.md") return "Personalidade";
    if (name.endsWith(".py")) return "Código Python";
    if (name.endsWith(".ts")) return "Código TypeScript";
    if (name.endsWith(".json")) return "Dados JSON";
    return "Arquivo";
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-all">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
          <Bot className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-foreground font-semibold">{agent.name}</h3>
            {agent.id && <span className="text-xs text-muted-foreground font-mono">/{agent.id}</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5 truncate">
            {agent.description || "Agente YOS"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {agent.hasMemory && (
            <span className="flex items-center gap-1 text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded-lg border border-blue-500/20" title="Tem memória">
              <Brain className="w-3 h-3" /> MEM
            </span>
          )}
          {agent.hasSoul && (
            <span className="flex items-center gap-1 text-xs text-pink-400 bg-pink-500/10 px-2 py-1 rounded-lg border border-pink-500/20" title="Tem personalidade">
              <Heart className="w-3 h-3" /> SOUL
            </span>
          )}
          {agent.hasCode && (
            <span className="flex items-center gap-1 text-xs text-success bg-success/10 px-2 py-1 rounded-lg border border-success/20" title="Tem código">
              <Code className="w-3 h-3" /> CODE
            </span>
          )}
          <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-lg">
            {agent.files.length} arquivos
          </span>
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border">
          {agent.files.map((file) => (
            <button
              key={file.name}
              onClick={() => onViewFile(file, agent.name, agent.id)}
              className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-muted/30 transition-colors border-b border-border/50 last:border-b-0"
            >
              {getFileIcon(file.type)}
              <span className="font-mono text-sm text-foreground/80 flex-1">{file.name}</span>
              <span className="text-xs text-muted-foreground">{getFileLabel(file.name)}</span>
              <span className="text-xs text-muted-foreground/50">{(file.size / 1024).toFixed(1)} KB</span>
              <Eye className="w-4 h-4 text-muted-foreground/30" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Skills Section ─────────────────────────────────────────────────
function SkillsSection() {
  const { data: skills, isLoading } = trpc.brain.getSkillsWithFiles.useQuery();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
            <div className="h-5 bg-muted rounded w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  if (!skills || skills.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <Wrench className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
        <p className="text-muted-foreground">Nenhuma skill encontrada no repositório</p>
        <p className="text-xs text-muted-foreground/50 mt-1">Configure BRAIN_REPO_PATH no ambiente</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {skills.map((skill) => (
        <div
          key={skill.id}
          className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-all"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
              <Wrench className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h4 className="text-foreground font-medium text-sm">{skill.name}</h4>
              <span className="text-xs text-muted-foreground font-mono">{skill.id}</span>
            </div>
          </div>
          {skill.description && (
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{skill.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {skill.files.map((f) => (
              <span
                key={f.name}
                className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded font-mono"
              >
                {f.name}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Monitoring Tab Content ─────────────────────────────────────────
function MonitoringTab({
  onSelectAgent,
}: {
  onSelectAgent: (id: string) => void;
}) {
  const { data: agentsList, isLoading, refetch } = trpc.agents.list.useQuery();
  const { data: metrics } = trpc.agents.metrics.useQuery();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredAgents = (agentsList || []).filter((agent) => {
    const matchesSearch = !searchQuery ||
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (agent.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || agent.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-5">
      {/* Metrics Summary */}
      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Total",    value: metrics.total,   color: "text-foreground", filter: "all" },
            { label: "Online",   value: metrics.online,  color: "text-success",  filter: "online" },
            { label: "Ocupados", value: metrics.busy,    color: "text-primary",    filter: "busy" },
            { label: "Com Erro", value: metrics.error,   color: "text-red-400",    filter: "error" },
            { label: "Offline",  value: metrics.offline, color: "text-muted-foreground", filter: "offline" },
          ].map(({ label, value, color, filter }) => (
            <button
              key={label}
              onClick={() => setStatusFilter(statusFilter === filter ? "all" : filter)}
              className={`bg-card border rounded-xl p-4 text-center transition-all ${
                statusFilter === filter ? "border-primary/40 ring-1 ring-primary/20" : "border-border hover:border-primary/20"
              }`}
            >
              <p className={`text-2xl font-bold tracking-tight ${color}`}>{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </button>
          ))}
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar agentes por nome, tipo ou descrição..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground bg-card border border-border rounded-lg hover:border-primary/30 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        {statusFilter !== "all" && (
          <button
            onClick={() => setStatusFilter("all")}
            className="flex items-center gap-1.5 px-3 py-2.5 text-xs text-muted-foreground hover:text-foreground bg-card border border-border rounded-lg hover:border-primary/30 transition-colors"
          >
            <Filter className="w-3.5 h-3.5" />
            Limpar
          </button>
        )}
      </div>

      {/* Agent Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-muted rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-muted rounded w-2/3 mb-1.5" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
              </div>
              <div className="h-3 bg-muted rounded w-full mb-2" />
              <div className="h-3 bg-muted rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : filteredAgents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAgents.map((agent) => {
            const cfg = statusConfig[agent.status] || statusConfig.offline;
            const successRate = agent.successRate || 0;
            return (
              <button
                key={agent.id}
                onClick={() => onSelectAgent(agent.id)}
                className={`bg-card border rounded-xl p-5 transition-all duration-200 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 text-left cursor-pointer ${cfg.border}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-lg ${cfg.bg}`}>
                      {agentTypeIcons[agent.type] || "🤖"}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground text-sm truncate">{agent.name}</h3>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {agentTypeLabels[agent.type] || agent.type}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${cfg.color} ${cfg.border} bg-transparent`}>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
                    {cfg.label}
                  </div>
                </div>

                {agent.description && (
                  <p className="text-xs text-muted-foreground mb-4 line-clamp-2 leading-relaxed">
                    {agent.description}
                  </p>
                )}

                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                  <div className="text-center">
                    <p className="text-sm font-bold text-foreground">{agent.tasksCompleted || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Execuções</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-sm font-bold ${successRate >= 80 ? "text-success" : successRate >= 50 ? "text-yellow-400" : "text-red-400"}`}>
                      {successRate}%
                    </p>
                    <p className="text-[10px] text-muted-foreground">Sucesso</p>
                    <SuccessBar rate={successRate} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-foreground">{formatDuration(agent.avgDuration)}</p>
                    <p className="text-[10px] text-muted-foreground">Média</p>
                  </div>
                </div>

                <div className="mt-3 pt-2 border-t border-border/50 flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">Última atividade</span>
                  <span className="text-[10px] text-muted-foreground">{formatTimeAgo(agent.lastActiveAt)}</span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-16 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Bot className="w-8 h-8 text-primary opacity-60" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {agentsList && agentsList.length > 0 ? "Nenhum agente encontrado" : "Nenhum agente registrado"}
          </h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            {agentsList && agentsList.length > 0
              ? "Tente ajustar os filtros ou a busca"
              : "Os agentes serão registrados automaticamente quando o Brain executar tarefas."}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Files Tab Content ──────────────────────────────────────────────
function FilesTab({
  onViewFile,
}: {
  onViewFile: (file: { name: string; type: string; size: number; content: string }, agentName: string, agentId?: string) => void;
}) {
  const { data: agents, isLoading } = trpc.brain.getAgentsWithFiles.useQuery();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-muted" />
              <div className="flex-1">
                <div className="h-5 bg-muted rounded w-1/4 mb-2" />
                <div className="h-4 bg-muted/50 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!agents || agents.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Sparkles className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">Nenhum agente encontrado</h3>
        <p className="text-sm text-muted-foreground/50">
          Verifique se o Brain API (ym-brain-api) está online e com BRAIN_API_KEY configurada
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{agents.length}</p>
          <p className="text-xs text-muted-foreground">Agentes</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">
            {agents.reduce((sum, a) => sum + a.files.length, 0)}
          </p>
          <p className="text-xs text-muted-foreground">Arquivos</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-400">
            {agents.filter((a) => a.hasMemory).length}
          </p>
          <p className="text-xs text-muted-foreground">Com Memória</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-pink-400">
            {agents.filter((a) => a.hasSoul).length}
          </p>
          <p className="text-xs text-muted-foreground">Com Soul</p>
        </div>
      </div>

      {agents.map((agent, idx) => (
        <AgentFileCard
          key={('id' in agent ? agent.id : undefined) || agent.name || idx}
          agent={agent}
          onViewFile={onViewFile}
        />
      ))}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────
export function Agents() {
  const [activeTab, setActiveTab] = useState<"monitoring" | "files">("monitoring");
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [viewingFile, setViewingFile] = useState<{
    file: { name: string; type: string; size: number; content: string };
    agentName: string;
    agentId?: string;
  } | null>(null);

  const { data: yosAgents } = trpc.brain.getAgentsWithFiles.useQuery();

  // If viewing detail, show detail view
  if (selectedAgentId) {
    return <AgentDetail agentId={selectedAgentId} onBack={() => setSelectedAgentId(null)} />;
  }

  const tabs = [
    { id: "monitoring" as const, label: "Monitoramento", icon: Activity },
    { id: "files" as const, label: "Arquivos YOS", icon: FolderOpen, count: yosAgents?.length },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
          <Bot className="w-6 h-6 text-primary" />
          Agentes
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Monitore, gerencie e inspecione os agentes de automação do ecossistema YOS
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "text-foreground border-b-2 border-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && (
              <span className="text-xs bg-muted/50 px-1.5 py-0.5 rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "monitoring" && (
        <MonitoringTab onSelectAgent={(id) => setSelectedAgentId(id)} />
      )}

      {activeTab === "files" && (
        <FilesTab onViewFile={(file, agentName, agentId) => setViewingFile({ file, agentName, agentId })} />
      )}

      {/* File Viewer Modal */}
      {viewingFile && (
        <FileViewer
          file={viewingFile.file}
          agentName={viewingFile.agentName}
          agentId={viewingFile.agentId}
          onClose={() => setViewingFile(null)}
        />
      )}
    </div>
  );
}
